import React from 'react';
import { Button, Group, Modal, Stack, Text, TextInput, Alert } from '@mantine/core';
import { useForm } from '@mantine/form';
import { inject, observer } from 'mobx-react';
import { t } from 'i18next';
import { IconAlertCircle } from '@tabler/icons-react';
import ChangePassword from '../ChangePassword/ChangePassword.tsx';

const ForgotPassword: React.FC = inject()(
    observer(() => {
        return <ChangePassword />;
    }),
);

type ForgotPasswordModalProps = {
    opened: boolean;
    onClose: () => void;
    onSend: (email: string) => void;
    error?: string | null;
    isSubmitting?: boolean;
};

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = observer(({ opened, onClose, onSend, error, isSubmitting }) => {
    const form = useForm({
        initialValues: {
            email: '',
        },

        validate: {
            email: (value) =>
                !value
                    ? t('formError.requiredEmail')
                    : /^\S+@\S+\.\S+$/.test(value)
                      ? null
                      : t('formError.invalidEmail'),
        },
    });

    const handleSubmit = (values: { email: string }) => {
        onSend(values.email);
        // Don't close the modal immediately - let the parent handle it after success/error
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('modal.forgot.forgotPasswordTitle')}
            size="sm"
            radius="md"
            withCloseButton={false}
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="sm">
                    <Text size="sm">{t('modal.forgot.forgotPasswordDescription')}</Text>

                    <TextInput
                        label={t('formGlobal.email')}
                        placeholder={t('formGlobal.enterEmailAddress')}
                        {...form.getInputProps('email')}
                        required
                    />

                    {error && (
                        <Alert variant="light" color="red" icon={<IconAlertCircle size={16} />}>
                            {error}
                        </Alert>
                    )}

                    <Group justify="space-between" mt="md">
                        <Button variant="default" onClick={onClose}>
                            {t('button.cancel')}
                        </Button>
                        <Button type="submit" loading={isSubmitting}>{t('button.sendResetLink')}</Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
});

export default ForgotPassword;
